import React, { useState } from "react";
import PropTypes from "prop-types";
import BootstrapTable from "react-bootstrap-table-next";
import filterFactory from "react-bootstrap-table2-filter";
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
  SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import "./PaginationTable.css";
import { withTranslation } from "react-i18next";
import { useEffect } from "react";
import StorageService from "../../services/StorageService";

/**
 * Generic PaginationTable with search functionality
 * Required props:
 * @property {function} getData - callback to fetch the data with a callback param which takes data as a param
 * @property {number} sizePerPage - number of results / entries per pagination page
 * @property {number} paginationSize - number of next pages shown e.g. 2 : 1 , 2, ... Prev, Next, First, Last
 * @property {string} keyField - the primaryKey field of the data, e.g. Id
 * @property {array} columns - configuration for the columns, e.g. :
 *               [{
 *                 dataField: 'Id',
 *                 text: 'Id'
 *               }, {
 *                 dataField: 'Name',
 *                 text: 'Name',
 *               }, {
 *                 dataField: 'Age',
 *                 text: 'Age',
 *                 sort: true
 *               }]
 * @augments {Component<Props, State>}
 */

const PaginationTable = (props) => {
  const { t } = props;
  const view = typeof props["view"] !== "undefined" ? props.view : props.id;
  const noCache = typeof props["noFilterCache"] !== "undefined" && props["noFilterCache"];
  const [sizePerPage, setSizePerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(
    StorageService.getPaginationTableIndex(view) || 1
  );

  props.columns.forEach((c) => {
    if (c.sort) {
      c.onSort = (field, order) => {
        let sort = [{ dataField: field, order }];
        if (!noCache) StorageService.setTableConfigValue(props.id, "sort", sort);
      };
    }
  });

  let filters = StorageService.getTableConfigValue(props.id, "filter");
  props.columns.forEach((c) => {
    if (c.filter?.props && filters) {
      let f = filters.find((x) => x.dataField === c.dataField);
      if (f) {
        c.filter.props.defaultValue = f.filterValue;
      }
    }
  });

  props.columns.forEach((c) => {
    if (c.filter?.props) {
      c.filter.props.onFilter = (filterValue) => {
        if (!noCache) StorageService.setPaginationTableIndex(view, 1);
        let cachedFilter =
          StorageService.getTableConfigValue(props.id, "filter") || [];
        let isNew = true;
        const updatedCachedFilter = cachedFilter.map(f => {
          if (f.dataField === c.dataField) {
            isNew = false;
            return { dataField: c.dataField, filterValue }
          }
          return Object.assign({}, f);
        })
        if (isNew) updatedCachedFilter.push({ dataField: c.dataField, filterValue });
        const filter = updatedCachedFilter
            .filter(d => (typeof d["filterValue"] === "string" && d.filterValue.length > 0) || typeof d["filterValue"] === "object");
        if (!noCache) StorageService.setTableConfigValue(props.id, "filter", filter);
      };
    }
  });

  const maxPage =
    props.data.length > 0 ? Math.ceil(props.data.length / sizePerPage) : 1;
  const options = {
    alwaysShowAllBtns: true,
    custom: true,
    sizePerPageList: [
      {
        text: "10",
        value: 10,
      },
      {
        text: "25",
        value: 25,
      },
      {
        text: "50",
        value: 50,
      },
      {
        text: "100",
        value: 100,
      },
      {
        text: t("PaginationTable.All"),
        value: props.data.length,
      },
    ],
    sizePerPage: sizePerPage,
    onSizePerPageChange: (size, page) => {
      StorageService.setTableConfigValue(props.id, "sizePerPage", size);
    },
    onPageChange: (page) => {
      setCurrentPage(page);
      if (!noCache) StorageService.setPaginationTableIndex(view, page);
    },
    paginationSize: props.paginationSize,
    pageStartIndex: 1,
    prePage: "<",
    nextPage: ">",
    firstPage: "<<",
    lastPage: ">>",
    page: currentPage > maxPage ? 1 : currentPage,
    totalSize: props.data?.length || 0,
  };
  const rowSelectionConfig = props.rowSelectionConfig;
  useEffect(() => {
    let sizePerPage = Number(
      StorageService.getTableConfigValue(props.id, "sizePerPage") ||
        props.sizePerPage ||
        10
    );
    setSizePerPage(sizePerPage);
  }, []);

  return (
    <PaginationProvider pagination={paginationFactory(options)}>
      {({ paginationProps, paginationTableProps }) => (
        <div>
          <BootstrapTable
            keyField={props.keyField}
            data={props.data || []}
            columns={props.columns}
            filter={filterFactory()}
            bootstrap4={true}
            selectRow={rowSelectionConfig}
            bordered={false}
            noDataIndication={
              props.noDataFoundLabel || t("PaginationTable.NoData")
            }
            defaultSorted={StorageService.getTableConfigValue(props.id, "sort")}
            {...paginationTableProps}
          />
          <PaginationListStandalone {...paginationProps} />
          <SizePerPageDropdownStandalone
            className={
              props.data.length > 0
                ? "sizePerPage-dropdown"
                : "sizePerPage-dropdown d-none"
            }
            {...paginationProps}
          />
        </div>
      )}
    </PaginationProvider>
  );
};

PaginationTable.propTypes = {
  getData: PropTypes.func,
  sizePerPage: PropTypes.number,
  paginationSize: PropTypes.number,
  keyField: PropTypes.string,
  columns: PropTypes.array,
  rowSelectionConfig: PropTypes.object,
};

export default withTranslation()(PaginationTable);
